import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../../components/layouts/AdminLayout';
import { getProduct, createProduct, updateProduct } from '../../../services/productService';
import { getCategories } from '../../../services/categoryService';
import { getSubcategories } from '../../../services/subcategoryService';
import { getTags } from '../../../services/tagService';
import { toast } from 'react-toastify';
import { FiSave, FiX, FiPlus, FiTrash2, FiUpload, FiChevronDown, FiChevronUp, FiCheck } from 'react-icons/fi';

const ProductsFormPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditMode = !!id;

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        price: '',
        discount: 0,
        category: '',
        subcategory: '',
        brand: '',
        tags: [],
        rating: '4.0',
        imageUrl: '',
        affiliateLink: '',
        isFeatured: false,
        isActive: true,
        // Expandable content fields
        highlights: [],
        pros: [],
        cons: [],
        keyIngredients: [],
        benefits: [],
        suitability: [],
        size: [],
        fragrance: [],
        attributes: [],
        radianceTake: '',
        // Additional fields
        reviewCount: 0,
        badges: [],
        currency: 'USD',
        // Category-specific fields
        categoryFields: {}
    });

    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);
    const [availableTags, setAvailableTags] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Dummy variables to prevent ESLint errors from old bulk upload code
    const [showBulkUpload] = useState(false);
    const [bulkProducts] = useState([]);
    const [isUploading] = useState(false);
    const [submittingIndex] = useState(null);
    const [isSubmittingAll] = useState(false);
    const [bulkSubcategoriesMap] = useState({});
    const [bulkTagsMap] = useState({});
    const setBulkSubcategoriesMap = () => {};
    const setBulkTagsMap = () => {};
    const setBulkProducts = () => {};
    const setSubmittingIndex = () => {};
    const setIsSubmittingAll = () => {};
    const handleFileUpload = () => {};
    const Papa = { parse: () => {} };


    useEffect(() => {
        fetchCategories();
        if (isEditMode) {
            fetchProduct();
        }
    }, [id]);

    useEffect(() => {
        if (formData.category) {
            fetchSubcategories(formData.category);
            // Clear tags when category changes
            setAvailableTags([]);
            setFormData(prev => ({ ...prev, subcategory: '', tags: [] }));
        } else {
            setSubcategories([]);
            setAvailableTags([]);
            setFormData(prev => ({ ...prev, subcategory: '', tags: [] }));
        }
    }, [formData.category]);

    useEffect(() => {
        if (formData.category && formData.subcategory) {
            // Only fetch tags when both category and subcategory are selected
            fetchTags({
                category: formData.category,
                subcategory: formData.subcategory
            });
        } else {
            setAvailableTags([]);
        }
    }, [formData.subcategory]);

    const fetchCategories = async () => {
        try {
            const response = await getCategories();
            setCategories(response.data || []);
        } catch (error) {
            console.error('Error fetching categories:', error);
            toast.error('Failed to load categories');
        }
    };

    const getCategoryName = (categoryId) => {
        const category = categories.find(cat => cat._id === categoryId);
        return category ? category.name : '';
    };

    const fetchSubcategories = async (categoryId) => {
        try {
            const response = await getSubcategories(categoryId);
            setSubcategories(response.data || []);

            // Store these subcategories in our map for bulk operations
            setBulkSubcategoriesMap(prev => ({
                ...prev,
                [categoryId]: response.data || []
            }));

            return response.data || [];
        } catch (error) {
            console.error('Error fetching subcategories:', error);
            toast.error('Failed to load subcategories');
            return [];
        }
    };

    const fetchTags = async (params = {}) => {
        try {
            // Tags are tied to subcategories, so we need both category and subcategory
            if (!params.category || !params.subcategory) {
                setAvailableTags([]);
                return [];
            }

            const response = await getTags({
                subcategoryId: params.subcategory,
                categoryId: params.category
            });

            // Make sure we're getting tags specific to this subcategory
            const filteredTags = response.data ? response.data.filter(tag =>
                tag.subcategory === params.subcategory ||
                (tag.subcategory._id && tag.subcategory._id === params.subcategory)
            ) : [];

            setAvailableTags(filteredTags);

            // Store tags in our map for bulk operations
            const key = `${params.category}-${params.subcategory}`;
            setBulkTagsMap(prev => ({
                ...prev,
                [key]: filteredTags
            }));

            return filteredTags;
        } catch (error) {
            console.error('Error fetching tags:', error);
            toast.error('Failed to load tags');
            return [];
        }
    };

    const fetchProduct = async () => {
        try {
            setLoading(true);
            const response = await getProduct(id);
            const product = response.data;

            const categoryId = product.category?._id || product.category || '';
            const subcategoryId = product.subcategory?._id || product.subcategory || '';

            const productData = {
                name: product.name || '',
                description: product.description || '',
                price: product.price || '',
                discount: product.discount || 0,
                category: categoryId,
                subcategory: subcategoryId,
                brand: product.brand || '',
                tags: product.tags?.map(tag => (typeof tag === 'string' ? tag : tag._id)) || [],
                rating: product.rating ? String(Number(product.rating).toFixed(1)) : '4.0',
                imageUrl: product.imageUrl || '',
                affiliateLink: product.affiliateLink || '',
                isFeatured: product.isFeatured !== undefined ? product.isFeatured : false,
                isActive: product.isActive !== undefined ? product.isActive : true,
                // Expandable content fields
                highlights: product.highlights || [],
                pros: product.pros || [],
                cons: product.cons || [],
                keyIngredients: (product.keyIngredients || []).map(item => 
                    typeof item === 'string' 
                        ? { name: item, benefit: '' } 
                        : item
                ),
                benefits: product.benefits || [],
                suitability: product.suitability || [],
                size: product.size || [],
                fragrance: product.fragrance || [],
                attributes: product.attributes || [],
                radianceTake: product.radianceTake || '',
                // Additional fields
                reviewCount: product.reviewCount || 0,
                badges: product.badges || [],
                currency: product.currency || 'USD',
                shortDescription: product.shortDescription || ''
            };

            setFormData(productData);

            if (categoryId) {
                await fetchSubcategories(categoryId);

                if (subcategoryId) {
                    await fetchTags({
                        category: categoryId,
                        subcategory: subcategoryId
                    });
                } else {
                    await fetchTags({ category: categoryId });
                }
            }
        } catch (error) {
            console.error('Error fetching product:', error);
            toast.error('Failed to load product data');
            navigate('/admin/products');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (name === 'category') {
            setFormData(prev => ({
                ...prev,
                [name]: value,
                subcategory: '',  // Reset subcategory when category changes
                tags: [],  // Clear tags when category changes
                categoryFields: {}  // Clear category-specific fields when category changes
            }));
        } else if (name === 'subcategory') {
            setFormData(prev => ({
                ...prev,
                [name]: value,
                tags: []  // Clear tags when subcategory changes
            }));
        } else if (name.startsWith('categoryField_')) {
            // Handle category-specific fields
            const fieldName = name.replace('categoryField_', '');
            setFormData(prev => ({
                ...prev,
                categoryFields: {
                    ...prev.categoryFields,
                    [fieldName]: value
                }
            }));
        } else if (name === 'rating') {
            // Ensure rating preserves decimal values
            setFormData(prev => ({
                ...prev,
                [name]: value // Keep as string to preserve decimal input
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            }));
        }
    };

    const handleTagToggle = (tagId) => {
        setFormData(prev => {
            const tagIndex = prev.tags.indexOf(tagId);
            if (tagIndex === -1) {
                return { ...prev, tags: [...prev.tags, tagId] };
            } else {
                return { ...prev, tags: prev.tags.filter(id => id !== tagId) };
            }
        });
    };

    const validateForm = () => {
        if (!formData.name.trim()) {
            toast.error('Product name is required');
            return false;
        }

        if (!formData.description.trim()) {
            toast.error('Product description is required');
            return false;
        }

        if (!formData.brand.trim()) {
            toast.error('Brand is required');
            return false;
        }

        // Price is optional - skip validation
        // if (!formData.price || isNaN(formData.price) || parseFloat(formData.price) <= 0) {
        //     toast.error('Please enter a valid price');
        //     return false;
        // }

        if (!formData.category) {
            toast.error('Category is required');
            return false;
        }

        if (!formData.subcategory) {
            toast.error('Subcategory is required');
            return false;
        }

        if (!formData.imageUrl.trim()) {
            toast.error('Image URL is required');
            return false;
        }

        if (!formData.affiliateLink.trim()) {
            toast.error('Affiliate link is required');
            return false;
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        // Prepare data for submission
        const submitData = {
            ...formData,
            price: parseFloat(formData.price),
            discount: parseFloat(formData.discount) || 0,
            rating: parseFloat(formData.rating) || 4.0,
            // Ensure arrays are properly formatted and filter out empty strings
            highlights: (formData.highlights || []).filter(item => item.trim() !== ''),
            pros: (formData.pros || []).filter(item => item.trim() !== ''),
            cons: (formData.cons || []).filter(item => item.trim() !== ''),
            keyIngredients: (formData.keyIngredients || []).filter(item => 
                (typeof item === 'object' && item.name && item.name.trim() !== '') || 
                (typeof item === 'string' && item.trim() !== '')
            ),
            benefits: (formData.benefits || []).filter(item => item.trim() !== ''),
            suitability: (formData.suitability || []).filter(item => item.trim() !== ''),
            // Send fragrance and size as arrays like other expandable fields
            fragrance: (formData.fragrance || []).filter(item => item.trim() !== ''),
            size: (formData.size || []).filter(item => item.trim() !== ''),
            attributes: (formData.attributes || []).filter(item => item.trim() !== ''),
            radianceTake: formData.radianceTake || '',
            badges: formData.badges || [],
            currency: formData.currency || 'USD',
            reviewCount: parseInt(formData.reviewCount) || 0,
        };

        // Debug logging
        console.log('Submitting product data:', submitData);

        try {
            setSubmitting(true);

            if (isEditMode) {
                await updateProduct(id, submitData);
                toast.success(`Product "${formData.name}" updated successfully`);
            } else {
                await createProduct(submitData);
                toast.success(`Product "${formData.name}" created successfully`);
            }

            navigate('/admin/products');
        } catch (error) {
            console.error('Error saving product:', error);
            toast.error(error.response?.data?.message || 'Failed to save product');
        } finally {
            setSubmitting(false);
        }
    };


    const handleBulkInputChange = async (index, field, value) => {
        const updatedProducts = [...bulkProducts];

        if (field === 'isActive' || field === 'isFeatured') {
            updatedProducts[index][field] = value;
        } else if (field === 'price' || field === 'discount' || field === 'rating') {
            updatedProducts[index][field] = parseFloat(value) || 0;
        } else if (field === 'category') {
            // When category changes, update category and reset subcategory and tags
            updatedProducts[index].category = value;
            updatedProducts[index].subcategory = '';
            updatedProducts[index].tags = [];
            updatedProducts[index].availableTags = [];

            // Fetch subcategories for this category if not already in our map
            if (value) {
                try {
                    let subcats;
                    if (!bulkSubcategoriesMap[value]) {
                        subcats = await fetchSubcategories(value);
                    } else {
                        subcats = bulkSubcategoriesMap[value];
                    }

                    // Update the product's available subcategories
                    updatedProducts[index].subcategories = subcats || [];
                } catch (error) {
                    console.error('Error fetching subcategories:', error);
                    updatedProducts[index].subcategories = [];
                }
            } else {
                updatedProducts[index].subcategories = [];
            }
        } else if (field === 'subcategory') {
            // When subcategory changes, update subcategory and reset tags
            updatedProducts[index].subcategory = value;
            updatedProducts[index].tags = [];

            // Fetch tags for this category/subcategory if not already in our map
            if (value && updatedProducts[index].category) {
                const key = `${updatedProducts[index].category}-${value}`;

                try {
                    if (!bulkTagsMap[key]) {
                        const response = await getTags({
                            categoryId: updatedProducts[index].category,
                            subcategoryId: value
                        });

                        // Make sure we only get tags for this specific subcategory
                        const filteredTags = response.data ? response.data.filter(tag =>
                            tag.subcategory === value ||
                            (tag.subcategory._id && tag.subcategory._id === value)
                        ) : [];

                        // Update our tags map
                        setBulkTagsMap(prev => ({
                            ...prev,
                            [key]: filteredTags
                        }));
                    }
                } catch (error) {
                    console.error('Error fetching tags:', error);
                }
            }
        } else if (field === 'tag') {
            // Handle individual tag toggling
            const tagId = value;
            const tagIndex = updatedProducts[index].tags.indexOf(tagId);

            if (tagIndex === -1) {
                updatedProducts[index].tags = [...updatedProducts[index].tags, tagId];
            } else {
                updatedProducts[index].tags = updatedProducts[index].tags.filter(id => id !== tagId);
            }
        } else {
            updatedProducts[index][field] = value;
        }

        setBulkProducts(updatedProducts);
    };

    const handleRemoveRow = (index) => {
        const updatedProducts = [...bulkProducts];
        updatedProducts.splice(index, 1);
        setBulkProducts(updatedProducts);
    };

    const handleAddEmptyRow = () => {
        setBulkProducts([
            ...bulkProducts,
            {
                id: `temp-${Date.now()}`,
                name: '',
                description: '',
                price: '',
                discount: 0,
                category: '',
                subcategory: '',
                brand: '',
                tags: [],
                rating: '4.0',
                imageUrl: '',
                affiliateLink: '',
                isFeatured: false,
                isActive: true,
                status: 'pending',
                subcategories: []
            }
        ]);
    };

    const validateBulkProduct = (product) => {
        if (!product.name.trim()) {
            return { valid: false, message: 'Name is required' };
        }
        if (!product.description.trim()) {
            return { valid: false, message: 'Description is required' };
        }
        if (!product.brand.trim()) {
            return { valid: false, message: 'Brand is required' };
        }
        // Price is optional - skip validation
        // if (!product.price || isNaN(product.price) || parseFloat(product.price) <= 0) {
        //     return { valid: false, message: 'Please enter a valid price' };
        // }
        if (!product.category) {
            return { valid: false, message: 'Category is required' };
        }
        if (!product.subcategory) {
            return { valid: false, message: 'Subcategory is required' };
        }
        if (!product.imageUrl.trim()) {
            return { valid: false, message: 'Image URL is required' };
        }
        if (!product.affiliateLink.trim()) {
            return { valid: false, message: 'Affiliate link is required' };
        }

        // Check if category exists
        const categoryExists = categories.some(c => c._id === product.category);
        if (!categoryExists) {
            return { valid: false, message: 'Selected category doesn\'t exist' };
        }

        // Check if subcategory exists and belongs to the category
        if (bulkSubcategoriesMap[product.category]) {
            const subcategoryExists = bulkSubcategoriesMap[product.category].some(s => s._id === product.subcategory);
            if (!subcategoryExists) {
                return { valid: false, message: 'Selected subcategory doesn\'t exist or is not part of the selected category' };
            }
        } else {
            return { valid: false, message: 'Category subcategories not loaded yet' };
        }

        return { valid: true };
    };

    const handleSubmitRow = async (index) => {
        const product = bulkProducts[index];
        const validation = validateBulkProduct(product);

        if (!validation.valid) {
            toast.error(`Row ${index + 1}: ${validation.message}`);
            return;
        }

        setSubmittingIndex(index);

        try {
            const updatedProducts = [...bulkProducts];
            updatedProducts[index].status = 'submitting';
            setBulkProducts(updatedProducts);

            // Prepare data for submission
            const submitData = {
                name: product.name,
                description: product.description,
                price: parseFloat(product.price),
                discount: parseFloat(product.discount) || 0,
                category: product.category,
                subcategory: product.subcategory,
                brand: product.brand,
                tags: product.tags,
                rating: parseFloat(product.rating) || 4.0,
                imageUrl: product.imageUrl,
                affiliateLink: product.affiliateLink,
                isFeatured: product.isFeatured,
                isActive: product.isActive
            };

            await createProduct(submitData);

            updatedProducts[index].status = 'success';
            setBulkProducts(updatedProducts);
            toast.success(`Product "${product.name}" created successfully`);
        } catch (error) {
            console.error('Error creating product:', error);
            const updatedProducts = [...bulkProducts];
            updatedProducts[index].status = 'error';
            updatedProducts[index].errorMessage = error.response?.data?.message || 'Failed to create product';
            setBulkProducts(updatedProducts);
            toast.error(`Failed to create product "${product.name}": ${updatedProducts[index].errorMessage}`);
        } finally {
            setSubmittingIndex(null);
        }
    };

    const handleSubmitAll = async () => {
        if (bulkProducts.length === 0) {
            toast.warning('No products to submit');
            return;
        }

        // Validate all products first
        for (let i = 0; i < bulkProducts.length; i++) {
            const product = bulkProducts[i];
            if (product.status === 'success') continue; // Skip already successful ones

            const validation = validateBulkProduct(product);
            if (!validation.valid) {
                toast.error(`Row ${i + 1}: ${validation.message}`);
                return;
            }
        }

        setIsSubmittingAll(true);

        try {
            const updatedProducts = [...bulkProducts];
            let successCount = 0;
            let errorCount = 0;

            for (let i = 0; i < updatedProducts.length; i++) {
                const product = updatedProducts[i];

                // Skip already processed products
                if (product.status === 'success') {
                    successCount++;
                    continue;
                }

                try {
                    setSubmittingIndex(i);

                    // Update status to submitting
                    updatedProducts[i].status = 'submitting';
                    setBulkProducts([...updatedProducts]);

                    // Prepare data for submission
                    const submitData = {
                        name: product.name,
                        description: product.description,
                        price: parseFloat(product.price),
                        discount: parseFloat(product.discount) || 0,
                        category: product.category,
                        subcategory: product.subcategory,
                        brand: product.brand,
                        tags: product.tags,
                        rating: parseFloat(product.rating) || 4.0,
                        imageUrl: product.imageUrl,
                        affiliateLink: product.affiliateLink,
                        isFeatured: product.isFeatured,
                        isActive: product.isActive
                    };

                    await createProduct(submitData);

                    updatedProducts[i].status = 'success';
                    successCount++;
                } catch (error) {
                    console.error('Error creating product:', error);
                    updatedProducts[i].status = 'error';
                    updatedProducts[i].errorMessage = error.response?.data?.message || 'Failed to create product';
                    errorCount++;
                }

                // Update the state after each operation
                setBulkProducts([...updatedProducts]);
            }

            if (successCount > 0) {
                toast.success(`Successfully created ${successCount} products`);
            }
            if (errorCount > 0) {
                toast.error(`Failed to create ${errorCount} products`);
            }
        } finally {
            setIsSubmittingAll(false);
            setSubmittingIndex(null);
        }
    };

    const downloadSampleCSV = () => {
        const headers = [
            'name', 'brand', 'description', 'price', 'discount',
            'category', 'subcategory', 'tags', 'rating',
            'imageUrl', 'affiliateLink', 'isFeatured', 'isActive'
        ];

        // Get real category, subcategory and tag names for the sample if available
        const categoryName1 = categories.length > 0 ? categories[0].name : 'Skincare';
        const categoryName2 = categories.length > 1 ? categories[1].name : 'Hair Care';

        // Try to get subcategories for each category
        let subcategoryName1 = 'Cleansers';
        let subcategoryName2 = 'Shampoos';

        if (categories.length > 0 && bulkSubcategoriesMap[categories[0]._id]?.length > 0) {
            subcategoryName1 = bulkSubcategoriesMap[categories[0]._id][0].name;
        }

        if (categories.length > 1 && bulkSubcategoriesMap[categories[1]._id]?.length > 0) {
            subcategoryName2 = bulkSubcategoriesMap[categories[1]._id][0].name;
        }

        // Try to get tags for each subcategory
        let tagsStr1 = 'Anti-Aging,Sensitive';
        let tagsStr2 = 'Volume,Hydrating';

        const key1 = categories.length > 0 && bulkSubcategoriesMap[categories[0]._id]?.length > 0 ?
            `${categories[0]._id}-${bulkSubcategoriesMap[categories[0]._id][0]._id}` : '';

        const key2 = categories.length > 1 && bulkSubcategoriesMap[categories[1]._id]?.length > 0 ?
            `${categories[1]._id}-${bulkSubcategoriesMap[categories[1]._id][0]._id}` : '';

        if (key1 && bulkTagsMap[key1]?.length > 0) {
            tagsStr1 = bulkTagsMap[key1].slice(0, 2).map(t => t.name).join(',');
        }

        if (key2 && bulkTagsMap[key2]?.length > 0) {
            tagsStr2 = bulkTagsMap[key2].slice(0, 2).map(t => t.name).join(',');
        }

        const sampleData = [
            {
                name: 'Anti-aging Serum',
                brand: 'BrandName',
                description: 'This is a product description',
                price: '29.99',
                discount: '0',
                category: categoryName1, // Using category name instead of ID
                subcategory: subcategoryName1, // Using subcategory name instead of ID
                tags: tagsStr1, // Using tag names instead of IDs
                rating: '4.3',
                imageUrl: 'https://example.com/image.jpg',
                affiliateLink: 'https://example.com/affiliate-link',
                isFeatured: 'false',
                isActive: 'true'
            },
            {
                name: 'Hydrating Moisturizer',
                brand: 'BrandName',
                description: 'This is a product description',
                price: '19.99',
                discount: '5',
                category: categoryName2, // Using category name instead of ID
                subcategory: subcategoryName2, // Using subcategory name instead of ID
                tags: tagsStr2, // Using tag names instead of IDs
                rating: '4.7',
                imageUrl: 'https://example.com/image.jpg',
                affiliateLink: 'https://example.com/affiliate-link',
                isFeatured: 'true',
                isActive: 'true'
            }
        ];

        const csvContent = Papa.unparse({
            fields: headers,
            data: sampleData
        });

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'product_template.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const clearBulkData = () => {
        if (window.confirm('Are you sure you want to clear all bulk data?')) {
            setBulkProducts([]);
        }
    };

    const getSubcategoriesForProduct = (product) => {
        // First, check if product has explicitly set subcategories
        if (product.subcategories && product.subcategories.length > 0) {
            return product.subcategories;
        }

        // Then check global subcategories map for the category
        if (product.category && bulkSubcategoriesMap[product.category]) {
            return bulkSubcategoriesMap[product.category];
        }

        // If no subcategories found, return empty array
        return [];
    };

    const getTagsForProduct = (product) => {
        // Check for subcategory-specific tags first
        if (product.category && product.subcategory) {
            const key = `${product.category}-${product.subcategory}`;
            if (bulkTagsMap[key]) {
                return bulkTagsMap[key];
            }
        }

        // If no subcategory-specific tags found but we have a category,
        // return an empty array since tags are tied to subcategories
        return [];
    };

    return (
        <AdminLayout>
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-800">
                    {isEditMode ? 'Edit Product' : 'Create Product'}
                </h1>

            </div>

            {loading ? (
                <div className="flex justify-center py-10">
                    <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary-600"></div>
                </div>
            ) : (
                <>
                    {/* Single Product Form */}
                    {(!showBulkUpload || isEditMode) && (
                        <div className="bg-[#f8f8f8] shadow-md rounded-lg overflow-hidden mb-6">
                            <form onSubmit={handleSubmit} className="p-6">
                                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                                    <div className="col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Product Name*
                                        </label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            className="input"
                                            required
                                            maxLength={200}
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Brand*
                                        </label>
                                        <input
                                            type="text"
                                            name="brand"
                                            value={formData.brand}
                                            onChange={handleChange}
                                            className="input"
                                            required
                                            maxLength={100}
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Price
                                        </label>
                                        <input
                                            type="number"
                                            name="price"
                                            value={formData.price}
                                            onChange={handleChange}
                                            className="input"
                                            min="0"
                                            step="0.01"
                                            placeholder="Optional"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Discount
                                        </label>
                                        <input
                                            type="number"
                                            name="discount"
                                            value={formData.discount}
                                            onChange={handleChange}
                                            className="input"
                                            min="0"
                                            step="0.01"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Category*
                                        </label>
                                        <select
                                            name="category"
                                            value={formData.category}
                                            onChange={handleChange}
                                            className="input"
                                            required
                                        >
                                            <option value="">Select a Category</option>
                                            {categories.map(category => (
                                                <option key={category._id} value={category._id}>
                                                    {category.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Subcategory*
                                        </label>
                                        <select
                                            name="subcategory"
                                            value={formData.subcategory}
                                            onChange={handleChange}
                                            className="input"
                                            disabled={!formData.category}
                                            required
                                        >
                                            <option value="">Select a Subcategory</option>
                                            {subcategories.map(subcategory => (
                                                <option key={subcategory._id} value={subcategory._id}>
                                                    {subcategory.name}
                                                </option>
                                            ))}
                                        </select>
                                        {!subcategories.length && formData.category && (
                                            <p className="mt-1 text-xs text-red-500">
                                                No subcategories found for this category
                                            </p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Rating (1.0 - 5.0)
                                        </label>
                                        <input
                                            type="number"
                                            name="rating"
                                            value={formData.rating}
                                            onChange={handleChange}
                                            className="input"
                                            min="1"
                                            max="5"
                                            step="0.1"
                                            placeholder="e.g., 4.3"
                                        />
                                        <p className="text-xs text-gray-500 mt-1">Enter rating with decimal, e.g., 4.3, 4.7</p>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Image URL*
                                        </label>
                                        <input
                                            type="text"
                                            name="imageUrl"
                                            value={formData.imageUrl}
                                            onChange={handleChange}
                                            className="input"
                                            placeholder="https://example.com/image.jpg"
                                            required
                                        />
                                        {formData.imageUrl && (
                                            <div className="mt-2">
                                                <img
                                                    src={formData.imageUrl}
                                                    alt="Preview"
                                                    className="h-24 w-24 object-cover rounded-md border border-gray-200"
                                                />
                                            </div>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Affiliate Link*
                                        </label>
                                        <input
                                            type="text"
                                            name="affiliateLink"
                                            value={formData.affiliateLink}
                                            onChange={handleChange}
                                            className="input"
                                            placeholder="https://example.com/affiliate"
                                            required
                                        />
                                    </div>

                                    <div className="col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Description*
                                        </label>
                                        <textarea
                                            name="description"
                                            value={formData.description}
                                            onChange={handleChange}
                                            className="input min-h-[100px]"
                                            required
                                            maxLength={2000}
                                        ></textarea>
                                        <p className="mt-1 text-xs text-gray-500">
                                            {formData.description.length}/2000 characters
                                        </p>
                                    </div>


                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Review Count
                                        </label>
                                        <input
                                            type="number"
                                            name="reviewCount"
                                            value={formData.reviewCount}
                                            onChange={handleChange}
                                            className="input"
                                            min="0"
                                            placeholder="e.g., 1250"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Currency
                                        </label>
                                        <select
                                            name="currency"
                                            value={formData.currency}
                                            onChange={handleChange}
                                            className="input"
                                        >
                                            <option value="USD">USD</option>
                                            <option value="CAD">CAD</option>
                                            <option value="EUR">EUR</option>
                                            <option value="GBP">GBP</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Product Badges
                                        </label>
                                        <div className="flex flex-wrap gap-2">
                                            {['derm_approved', 'editors_choice', 'best_seller', 'trending'].map(badge => (
                                                <label key={badge} className="flex items-center">
                                                    <input
                                                        type="checkbox"
                                                        checked={(formData.badges || []).includes(badge)}
                                                        onChange={(e) => {
                                                            const newBadges = e.target.checked
                                                                ? [...(formData.badges || []), badge]
                                                                : (formData.badges || []).filter(b => b !== badge);
                                                            setFormData(prev => ({ ...prev, badges: newBadges }));
                                                        }}
                                                        className="rounded border-gray-300 text-primary-600 mr-2"
                                                    />
                                                    <span className="text-sm capitalize">
                                                        {badge.replace('_', ' ')}
                                                    </span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Expandable Content Section */}
                                    <div className="col-span-2">
                                        <div className="border-t pt-6 mt-6">
                                            <h3 className="text-lg font-semibold text-gray-900 mb-4">
                                                Expandable Product Details
                                            </h3>
                                            <p className="text-sm text-gray-600 mb-6">
                                                Add detailed information that will appear when users click "More info" on the product card.
                                            </p>

                                            {/* Highlights Section */}
                                            <div className="mb-6">
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Highlights
                                                </label>
                                                <div className="space-y-2">
                                                    {(formData.highlights || []).map((highlight, index) => (
                                                        <div key={index} className="flex items-center gap-2">
                                                            <input
                                                                type="text"
                                                                value={highlight}
                                                                onChange={(e) => {
                                                                    const newHighlights = [...(formData.highlights || [])];
                                                                    newHighlights[index] = e.target.value;
                                                                    setFormData(prev => ({ ...prev, highlights: newHighlights }));
                                                                }}
                                                                className="input flex-1"
                                                                placeholder="e.g., Editor's Choice, Best for Sensitive Skin"
                                                                maxLength={100}
                                                            />
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    const newHighlights = (formData.highlights || []).filter((_, i) => i !== index);
                                                                    setFormData(prev => ({ ...prev, highlights: newHighlights }));
                                                                }}
                                                                className="text-red-500 hover:text-red-700"
                                                            >
                                                                <FiTrash2 size={16} />
                                                            </button>
                                                        </div>
                                                    ))}
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setFormData(prev => ({ ...prev, highlights: [...(prev.highlights || []), ''] }));
                                                        }}
                                                        className="text-primary-600 hover:text-primary-700 text-sm flex items-center"
                                                    >
                                                        <FiPlus size={16} className="mr-1" /> Add Highlight
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Pros Section */}
                                            <div className="mb-6">
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Pros (Benefits)
                                                </label>
                                                <div className="space-y-2">
                                                    {(formData.pros || []).map((pro, index) => (
                                                        <div key={index} className="flex items-center gap-2">
                                                            <input
                                                                type="text"
                                                                value={pro}
                                                                onChange={(e) => {
                                                                    const newPros = [...(formData.pros || [])];
                                                                    newPros[index] = e.target.value;
                                                                    setFormData(prev => ({ ...prev, pros: newPros }));
                                                                }}
                                                                className="input flex-1"
                                                                placeholder="e.g., Gentle on sensitive skin"
                                                                maxLength={200}
                                                            />
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    const newPros = (formData.pros || []).filter((_, i) => i !== index);
                                                                    setFormData(prev => ({ ...prev, pros: newPros }));
                                                                }}
                                                                className="text-red-500 hover:text-red-700"
                                                            >
                                                                <FiTrash2 size={16} />
                                                            </button>
                                                        </div>
                                                    ))}
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setFormData(prev => ({ ...prev, pros: [...(prev.pros || []), ''] }));
                                                        }}
                                                        className="text-primary-600 hover:text-primary-700 text-sm flex items-center"
                                                    >
                                                        <FiPlus size={16} className="mr-1" /> Add Pro
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Cons Section */}
                                            <div className="mb-6">
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Cons (Drawbacks)
                                                </label>
                                                <div className="space-y-2">
                                                    {(formData.cons || []).map((con, index) => (
                                                        <div key={index} className="flex items-center gap-2">
                                                            <input
                                                                type="text"
                                                                value={con}
                                                                onChange={(e) => {
                                                                    const newCons = [...(formData.cons || [])];
                                                                    newCons[index] = e.target.value;
                                                                    setFormData(prev => ({ ...prev, cons: newCons }));
                                                                }}
                                                                className="input flex-1"
                                                                placeholder="e.g., May cause initial dryness"
                                                                maxLength={200}
                                                            />
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    const newCons = (formData.cons || []).filter((_, i) => i !== index);
                                                                    setFormData(prev => ({ ...prev, cons: newCons }));
                                                                }}
                                                                className="text-red-500 hover:text-red-700"
                                                            >
                                                                <FiTrash2 size={16} />
                                                            </button>
                                                        </div>
                                                    ))}
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setFormData(prev => ({ ...prev, cons: [...(prev.cons || []), ''] }));
                                                        }}
                                                        className="text-primary-600 hover:text-primary-700 text-sm flex items-center"
                                                    >
                                                        <FiPlus size={16} className="mr-1" /> Add Con
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Key Ingredients Section */}
                                            <div className="mb-6">
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Ingredient Breakdown
                                                </label>
                                                <div className="space-y-4">
                                                    {(formData.keyIngredients || []).map((ingredient, index) => (
                                                        <div key={index} className="border border-gray-200 rounded-lg p-4">
                                                            <div className="flex items-start gap-4">
                                                                <div className="flex-1 space-y-3">
                                                                    <div>
                                                                        <label className="block text-sm font-medium text-gray-600 mb-1">
                                                                            Ingredient Name
                                                                        </label>
                                                                        <input
                                                                            type="text"
                                                                            value={ingredient.name || ''}
                                                                            onChange={(e) => {
                                                                                const newIngredients = [...(formData.keyIngredients || [])];
                                                                                newIngredients[index] = { 
                                                                                    ...newIngredients[index], 
                                                                                    name: e.target.value 
                                                                                };
                                                                                setFormData(prev => ({ ...prev, keyIngredients: newIngredients }));
                                                                            }}
                                                                            className="input w-full"
                                                                            placeholder="e.g., Hyaluronic Acid"
                                                                            maxLength={100}
                                                                        />
                                                                    </div>
                                                                    <div>
                                                                        <label className="block text-sm font-medium text-gray-600 mb-1">
                                                                            Benefit/Description
                                                                        </label>
                                                                        <textarea
                                                                            value={ingredient.benefit || ''}
                                                                            onChange={(e) => {
                                                                                const newIngredients = [...(formData.keyIngredients || [])];
                                                                                newIngredients[index] = { 
                                                                                    ...newIngredients[index], 
                                                                                    benefit: e.target.value 
                                                                                };
                                                                                setFormData(prev => ({ ...prev, keyIngredients: newIngredients }));
                                                                            }}
                                                                            className="input w-full"
                                                                            placeholder="e.g., Provides deep hydration and plumps the skin"
                                                                            rows={2}
                                                                            maxLength={200}
                                                                        />
                                                                    </div>
                                                                </div>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        const newIngredients = formData.keyIngredients.filter((_, i) => i !== index);
                                                                        setFormData(prev => ({ ...prev, keyIngredients: newIngredients }));
                                                                    }}
                                                                    className="text-red-500 hover:text-red-700 p-1"
                                                                    title="Remove ingredient"
                                                                >
                                                                    <FiTrash2 size={16} />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setFormData(prev => ({ ...prev, keyIngredients: [...(prev.keyIngredients || []), { name: '', benefit: '' }] }));
                                                        }}
                                                        className="text-primary-600 hover:text-primary-700 text-sm flex items-center"
                                                    >
                                                        <FiPlus size={16} className="mr-1" /> Add Ingredient
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Benefits Section */}
                                            <div className="mb-6">
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Benefits
                                                </label>
                                                <div className="space-y-2">
                                                    {(formData.benefits || []).map((benefit, index) => (
                                                        <div key={index} className="flex items-center gap-2">
                                                            <input
                                                                type="text"
                                                                value={benefit}
                                                                onChange={(e) => {
                                                                    const newBenefits = [...(formData.benefits || [])];
                                                                    newBenefits[index] = e.target.value;
                                                                    setFormData(prev => ({ ...prev, benefits: newBenefits }));
                                                                }}
                                                                className="input flex-1"
                                                                placeholder="e.g., Anti-aging, Moisturizing, Brightening"
                                                                maxLength={100}
                                                            />
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    const newBenefits = (formData.benefits || []).filter((_, i) => i !== index);
                                                                    setFormData(prev => ({ ...prev, benefits: newBenefits }));
                                                                }}
                                                                className="text-red-500 hover:text-red-700"
                                                            >
                                                                <FiTrash2 size={16} />
                                                            </button>
                                                        </div>
                                                    ))}
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setFormData(prev => ({ ...prev, benefits: [...(prev.benefits || []), ''] }));
                                                        }}
                                                        className="text-primary-600 hover:text-primary-700 text-sm flex items-center"
                                                    >
                                                        <FiPlus size={16} className="mr-1" /> Add Benefit
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Suitability Section */}
                                            <div className="mb-6">
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Best For (Suitability)
                                                </label>
                                                <div className="space-y-2">
                                                    {(formData.suitability || []).map((item, index) => (
                                                        <div key={index} className="flex items-center gap-2">
                                                            <input
                                                                type="text"
                                                                value={item}
                                                                onChange={(e) => {
                                                                    const newSuitability = [...(formData.suitability || [])];
                                                                    newSuitability[index] = e.target.value;
                                                                    setFormData(prev => ({ ...prev, suitability: newSuitability }));
                                                                }}
                                                                className="input flex-1"
                                                                placeholder="e.g., Oily Skin, Anti-aging"
                                                                maxLength={100}
                                                            />
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    const newSuitability = (formData.suitability || []).filter((_, i) => i !== index);
                                                                    setFormData(prev => ({ ...prev, suitability: newSuitability }));
                                                                }}
                                                                className="text-red-500 hover:text-red-700"
                                                            >
                                                                <FiTrash2 size={16} />
                                                            </button>
                                                        </div>
                                                    ))}
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setFormData(prev => ({ ...prev, suitability: [...(prev.suitability || []), ''] }));
                                                        }}
                                                        className="text-primary-600 hover:text-primary-700 text-sm flex items-center"
                                                    >
                                                        <FiPlus size={16} className="mr-1" /> Add Suitability
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Size Field */}
                                            <div className="mb-6">
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Size
                                                </label>
                                                <div className="space-y-2">
                                                    {(formData.size || []).map((item, index) => (
                                                        <div key={index} className="flex items-center space-x-2">
                                                            <input
                                                                type="text"
                                                                value={item}
                                                                onChange={(e) => {
                                                                    const newSize = [...(formData.size || [])];
                                                                    newSize[index] = e.target.value;
                                                                    setFormData(prev => ({ ...prev, size: newSize }));
                                                                }}
                                                                className="input flex-1"
                                                                placeholder="e.g., 30ml, 1.7 fl oz, 50g"
                                                                maxLength={50}
                                                            />
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    const newSize = (formData.size || []).filter((_, i) => i !== index);
                                                                    setFormData(prev => ({ ...prev, size: newSize }));
                                                                }}
                                                                className="text-red-500 hover:text-red-700 p-1"
                                                                title="Remove size"
                                                            >
                                                                <FiTrash2 size={16} />
                                                            </button>
                                                        </div>
                                                    ))}
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setFormData(prev => ({ 
                                                                ...prev, 
                                                                size: [...(prev.size || []), ''] 
                                                            }));
                                                        }}
                                                        className="text-primary-600 hover:text-primary-700 text-sm flex items-center"
                                                    >
                                                        <FiPlus size={16} className="mr-1" /> Add Size
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Fragrance Field */}
                                            <div className="mb-6">
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Fragrance
                                                </label>
                                                <div className="space-y-2">
                                                    {(formData.fragrance || []).map((item, index) => (
                                                        <div key={index} className="flex items-center space-x-2">
                                                            <input
                                                                type="text"
                                                                value={item}
                                                                onChange={(e) => {
                                                                    const newFragrance = [...(formData.fragrance || [])];
                                                                    newFragrance[index] = e.target.value;
                                                                    setFormData(prev => ({ ...prev, fragrance: newFragrance }));
                                                                }}
                                                                className="input flex-1"
                                                                placeholder="e.g., Unscented, Light Floral, Fresh Citrus"
                                                                maxLength={50}
                                                            />
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    const newFragrance = (formData.fragrance || []).filter((_, i) => i !== index);
                                                                    setFormData(prev => ({ ...prev, fragrance: newFragrance }));
                                                                }}
                                                                className="text-red-500 hover:text-red-700 p-1"
                                                                title="Remove fragrance"
                                                            >
                                                                <FiTrash2 size={16} />
                                                            </button>
                                                        </div>
                                                    ))}
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setFormData(prev => ({ 
                                                                ...prev, 
                                                                fragrance: [...(prev.fragrance || []), ''] 
                                                            }));
                                                        }}
                                                        className="text-primary-600 hover:text-primary-700 text-sm flex items-center"
                                                    >
                                                        <FiPlus size={16} className="mr-1" /> Add Fragrance
                                                    </button>
                                                </div>
                                            </div>


                                            {/* Radiance Take Section */}
                                            <div className="mb-6">
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Radiance Take
                                                </label>
                                                <textarea
                                                    value={formData.radianceTake || ''}
                                                    onChange={(e) => setFormData(prev => ({ ...prev, radianceTake: e.target.value }))}
                                                    className="input w-full h-32 resize-none"
                                                    placeholder="Our beauty experts' detailed take on this product..."
                                                    maxLength={1000}
                                                />
                                                <p className="text-xs text-gray-500 mt-1">
                                                    {formData.radianceTake?.length || 0}/1000 characters
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Tags
                                        </label>
                                        <div className="flex flex-wrap gap-2">
                                            {!formData.category ? (
                                                <p className="text-sm text-gray-500">
                                                    Select a category to see available tags
                                                </p>
                                            ) : !formData.subcategory ? (
                                                <p className="text-sm text-gray-500">
                                                    Select a subcategory to see available tags
                                                </p>
                                            ) : availableTags.length === 0 ? (
                                                <p className="text-sm text-gray-500">
                                                    No tags available for this subcategory
                                                </p>
                                            ) : (
                                                availableTags.map(tag => (
                                                    <button
                                                        key={tag._id}
                                                        type="button"
                                                        onClick={() => handleTagToggle(tag._id)}
                                                        className={`px-3 py-1 rounded-full text-sm font-medium ${formData.tags.includes(tag._id)
                                                            ? 'bg-primary-100 text-primary-800 border-primary-300'
                                                            : 'bg-gray-100 text-gray-800 border-gray-300'
                                                            } border hover:bg-primary-200`}
                                                    >
                                                        {tag.name}
                                                    </button>
                                                ))
                                            )}
                                        </div>
                                    </div>

                                    <div>
                                        <div className="flex items-center">
                                            <input
                                                type="checkbox"
                                                name="isActive"
                                                checked={formData.isActive}
                                                onChange={handleChange}
                                                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                            />
                                            <label className="ml-2 block text-sm text-gray-700">
                                                Active
                                            </label>
                                        </div>
                                    </div>

                                    <div>
                                        <div className="flex items-center">
                                            <input
                                                type="checkbox"
                                                name="isFeatured"
                                                checked={formData.isFeatured}
                                                onChange={handleChange}
                                                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                            />
                                            <label className="ml-2 block text-sm text-gray-700">
                                                Featured Product
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-6 flex items-center justify-end space-x-3">
                                    <button
                                        type="button"
                                        onClick={() => navigate('/admin/products')}
                                        className="btn btn-outline flex items-center"
                                    >
                                        <FiX className="mr-2" /> Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        className={`btn btn-primary flex items-center ${submitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                                    >
                                        {submitting ? (
                                            <>
                                                <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                                                Saving...
                                            </>
                                        ) : (
                                            <>
                                                <FiSave className="mr-2" /> Save
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {/* Bulk Upload Section - REMOVED */}
                    {false && (
                        <div className="bg-[#f8f8f8] shadow-md rounded-lg overflow-hidden mb-6">
                            <div className="p-6">
                                <h2 className="text-xl font-semibold mb-4">Bulk Create Products</h2>
                                <div className="mb-6">
                                    <p className="text-sm text-gray-600 mb-3">
                                        Upload a CSV file with product data or add entries manually.
                                    </p>
                                    <div className="flex flex-wrap items-center gap-4">
                                        <label className="btn btn-primary flex items-center cursor-pointer">
                                            <FiUpload className="mr-2" />
                                            Upload CSV
                                            <input
                                                type="file"
                                                accept=".csv"
                                                onChange={handleFileUpload}
                                                className="hidden"
                                                disabled={isUploading}
                                            />
                                        </label>

                                        <button
                                            onClick={downloadSampleCSV}
                                            className="btn btn-outline flex items-center text-sm"
                                        >
                                            <FiUpload className="mr-1" /> Download Template
                                        </button>

                                        <button
                                            onClick={handleAddEmptyRow}
                                            className="btn btn-outline flex items-center"
                                        >
                                            <FiPlus className="mr-2" /> Add Row
                                        </button>

                                        {bulkProducts.length > 0 && (
                                            <>
                                                <button
                                                    onClick={clearBulkData}
                                                    className="btn btn-outline flex items-center text-red-600 border-red-600 hover:bg-red-50"
                                                >
                                                    <FiTrash2 className="mr-2" /> Clear All
                                                </button>

                                                <button
                                                    onClick={handleSubmitAll}
                                                    disabled={isSubmittingAll || bulkProducts.length === 0}
                                                    className={`btn btn-primary flex items-center ml-auto ${isSubmittingAll ? 'opacity-70 cursor-not-allowed' : ''}`}
                                                >
                                                    {isSubmittingAll ? (
                                                        <>
                                                            <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                                                            Saving All...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <FiSave className="mr-2" /> Save All
                                                        </>
                                                    )}
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {isUploading ? (
                                    <div className="text-center py-10">
                                        <div className="inline-block animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary-600 mb-4"></div>
                                        <p>Parsing CSV...</p>
                                    </div>
                                ) : bulkProducts.length > 0 ? (
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Name*
                                                    </th>
                                                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Brand*
                                                    </th>
                                                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Price*
                                                    </th>
                                                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Category*
                                                    </th>
                                                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Subcategory*
                                                    </th>
                                                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Tags
                                                    </th>
                                                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Status
                                                    </th>
                                                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Actions
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-[#f8f8f8] divide-y divide-gray-200">
                                                {bulkProducts.map((product, index) => (
                                                    <tr key={product.id} className={`${product.status === 'error' ? 'bg-red-50' : product.status === 'success' ? 'bg-green-50' : ''}`}>
                                                        <td className="px-3 py-2">
                                                            <input
                                                                type="text"
                                                                value={product.name}
                                                                onChange={(e) => handleBulkInputChange(index, 'name', e.target.value)}
                                                                disabled={product.status === 'success' || product.status === 'submitting' || submittingIndex === index}
                                                                className="input py-1 px-2 text-sm w-40"
                                                                placeholder="Product name"
                                                                required
                                                            />
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            <input
                                                                type="text"
                                                                value={product.brand}
                                                                onChange={(e) => handleBulkInputChange(index, 'brand', e.target.value)}
                                                                disabled={product.status === 'success' || product.status === 'submitting' || submittingIndex === index}
                                                                className="input py-1 px-2 text-sm w-32"
                                                                placeholder="Brand"
                                                                required
                                                            />
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            <input
                                                                type="number"
                                                                value={product.price}
                                                                onChange={(e) => handleBulkInputChange(index, 'price', e.target.value)}
                                                                disabled={product.status === 'success' || product.status === 'submitting' || submittingIndex === index}
                                                                className="input py-1 px-2 text-sm w-20"
                                                                placeholder="0.00"
                                                                min="0.01"
                                                                step="0.01"
                                                                required
                                                            />
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            <select
                                                                value={product.category}
                                                                onChange={(e) => handleBulkInputChange(index, 'category', e.target.value)}
                                                                disabled={product.status === 'success' || product.status === 'submitting' || submittingIndex === index}
                                                                className={`input py-1 px-2 text-sm ${!product.category && product.categoryName ? 'border-yellow-400' : ''}`}
                                                                required
                                                            >
                                                                <option value="">Select Category</option>
                                                                {categories.map(category => (
                                                                    <option key={category._id} value={category._id}>
                                                                        {category.name}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                            {!product.category && product.categoryName && (
                                                                <p className="mt-1 text-xs text-yellow-600">
                                                                    Could not find "{product.categoryName}"
                                                                </p>
                                                            )}
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            <select
                                                                value={product.subcategory}
                                                                onChange={(e) => handleBulkInputChange(index, 'subcategory', e.target.value)}
                                                                disabled={!product.category || product.status === 'success' || product.status === 'submitting' || submittingIndex === index}
                                                                className={`input py-1 px-2 text-sm ${!product.subcategory && product.subcategoryName ? 'border-yellow-400' : ''}`}
                                                                required
                                                            >
                                                                <option value="">Select Subcategory</option>
                                                                {getSubcategoriesForProduct(product).map(subcategory => (
                                                                    <option key={subcategory._id} value={subcategory._id}>
                                                                        {subcategory.name}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                            {!product.subcategory && product.subcategoryName && (
                                                                <p className="mt-1 text-xs text-yellow-600">
                                                                    Could not find "{product.subcategoryName}"
                                                                </p>
                                                            )}
                                                            {!getSubcategoriesForProduct(product).length && product.category && (
                                                                <p className="mt-1 text-xs text-red-500">
                                                                    No subcategories found for this category
                                                                </p>
                                                            )}
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            {/* Tag selection section */}
                                                            <div className="relative">
                                                                {!product.category ? (
                                                                    <span className="text-xs text-gray-500">
                                                                        Select a category
                                                                    </span>
                                                                ) : !product.subcategory ? (
                                                                    <span className="text-xs text-gray-500">
                                                                        Select a subcategory
                                                                    </span>
                                                                ) : getTagsForProduct(product).length === 0 ? (
                                                                    <span className="text-xs text-gray-500">
                                                                        No tags available for this subcategory
                                                                    </span>
                                                                ) : (
                                                                    <div className="flex flex-wrap gap-1 max-w-xs">
                                                                        {product.tags.length > 0 ? (
                                                                            <div className="text-xs text-primary-600 font-medium">
                                                                                {product.tags.length} tags selected
                                                                                <button
                                                                                    className="ml-1 text-gray-500 hover:text-gray-700"
                                                                                    title="View/Edit Selected Tags"
                                                                                    onClick={(e) => {
                                                                                        e.preventDefault();
                                                                                        alert(`Selected tags: ${getTagsForProduct(product)
                                                                                            .filter(tag => product.tags.includes(tag._id))
                                                                                            .map(tag => tag.name)
                                                                                            .join(', ')
                                                                                            }`);
                                                                                    }}
                                                                                >
                                                                                    (View)
                                                                                </button>
                                                                            </div>
                                                                        ) : (
                                                                            <div className="flex flex-wrap gap-1">
                                                                                <span className="text-xs text-gray-500">
                                                                                    No tags selected
                                                                                </span>
                                                                            </div>
                                                                        )}

                                                                        {product.status !== 'success' && product.status !== 'submitting' && submittingIndex !== index && (
                                                                            <div className="relative group ml-2">
                                                                                <button
                                                                                    type="button"
                                                                                    className="text-primary-600 hover:text-primary-800 text-xs px-2 py-1 border border-primary-300 rounded"
                                                                                    onClick={(e) => {
                                                                                        e.preventDefault();
                                                                                        const tagsDropdown = document.getElementById(`tags-dropdown-${index}`);
                                                                                        tagsDropdown.classList.toggle('hidden');
                                                                                    }}
                                                                                >
                                                                                    Edit tags
                                                                                </button>

                                                                                <div id={`tags-dropdown-${index}`} className="hidden absolute z-10 bg-[#f8f8f8] border border-gray-200 shadow-lg rounded-md p-2 mt-1 max-h-48 overflow-y-auto w-48">
                                                                                    <div className="flex flex-col space-y-1">
                                                                                        {getTagsForProduct(product).map(tag => (
                                                                                            <label key={tag._id} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 px-2 py-1 rounded">
                                                                                                <input
                                                                                                    type="checkbox"
                                                                                                    checked={product.tags.includes(tag._id)}
                                                                                                    onChange={() => handleBulkInputChange(index, 'tag', tag._id)}
                                                                                                    className="h-3 w-3 text-primary-600 border-gray-300 rounded"
                                                                                                />
                                                                                                <span className="text-xs">{tag.name}</span>
                                                                                            </label>
                                                                                        ))}
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                )}

                                                                {product.tagNames && product.tagNames.length > 0 && product.tags.length < product.tagNames.length && (
                                                                    <p className="mt-1 text-xs text-yellow-600">
                                                                        Some tags were not found: {
                                                                            product.tagNames.filter(name =>
                                                                                !getTagsForProduct(product).some(tag =>
                                                                                    tag.name.toLowerCase() === name.toLowerCase()
                                                                                )
                                                                            ).join(', ')
                                                                        }
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            {product.status === 'submitting' || submittingIndex === index ? (
                                                                <span className="inline-flex items-center px-2 py-1 text-xs font-medium">
                                                                    <div className="animate-spin rounded-full h-3 w-3 border-t-2 border-b-2 border-primary-600 mr-1"></div>
                                                                    Submitting...
                                                                </span>
                                                            ) : product.status === 'success' ? (
                                                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                                    <FiCheck className="mr-1" />
                                                                    Success
                                                                </span>
                                                            ) : product.status === 'error' ? (
                                                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800" title={product.errorMessage}>
                                                                    <FiX className="mr-1" />
                                                                    Error
                                                                </span>
                                                            ) : (
                                                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                                                    Pending
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            <div className="flex space-x-2">
                                                                {product.status !== 'success' && (
                                                                    <>
                                                                        <button
                                                                            onClick={() => handleSubmitRow(index)}
                                                                            disabled={product.status === 'submitting' || isSubmittingAll || submittingIndex === index}
                                                                            className="text-primary-600 hover:text-primary-900 p-1"
                                                                            title="Save this product"
                                                                        >
                                                                            <FiSave size={16} />
                                                                        </button>
                                                                        <button
                                                                            onClick={() => handleRemoveRow(index)}
                                                                            disabled={product.status === 'submitting' || isSubmittingAll || submittingIndex === index}
                                                                            className="text-red-600 hover:text-red-900 p-1"
                                                                            title="Remove this row"
                                                                        >
                                                                            <FiTrash2 size={16} />
                                                                        </button>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="text-center py-8 text-gray-500">
                                        <p>No products added yet. Upload a CSV or add rows manually.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </>
            )}
        </AdminLayout>
    );
};

export default ProductsFormPage;